import axios from "axios";
import { BACKEND_URL } from "../config_and_helpers/config";
import { showToast } from "../config_and_helpers/helpers";
import { logError, logInfo } from "../config_and_helpers/logger";

const LOG_CONTEXT = "finance_reception_email_client";
const SETTINGS_URL = `${BACKEND_URL}finance-reception-email-settings/`;

/**
 * @typedef {Object} FinanceReceptionEmailSettings
 * @property {string} recipient_email - Primary To address; may be empty.
 * @property {string} recipient_name - Display name for digest greeting; may be empty.
 * @property {string[]} cc_emails - CC addresses copied on each digest.
 * @property {string} updated_at - ISO 8601 last save time (read-only).
 */

/**
 * Loads the singleton finance reception email settings.
 *
 * @param {string} token - API auth token.
 * @returns {Promise<{ success: true, data: FinanceReceptionEmailSettings } | { success: false, fieldErrors?: object }>}
 */
export const getFinanceReceptionEmailSettings = async (token) => {
  logInfo(LOG_CONTEXT, "getFinanceReceptionEmailSettings request", {});
  try {
    const response = await axios.get(SETTINGS_URL, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    logInfo(LOG_CONTEXT, "getFinanceReceptionEmailSettings success", {
      hasRecipient: Boolean(response.data?.recipient_email),
      hasRecipientName: Boolean(response.data?.recipient_name),
      ccCount: response.data?.cc_emails?.length ?? 0,
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      "getFinanceReceptionEmailSettings failed",
      error.response?.data ?? error,
    );
    return {
      success: false,
      fieldErrors: error.response?.data,
    };
  }
};

/**
 * Replaces finance reception email settings (full PUT).
 *
 * @param {string} token - API auth token.
 * @param {{ recipient_email: string, recipient_name: string, cc_emails: string[] }} payload - Settings body.
 * @returns {Promise<{ success: true, data: FinanceReceptionEmailSettings, toast: function } | { success: false, fieldErrors?: object }>}
 */
export const updateFinanceReceptionEmailSettings = async (token, payload) => {
  logInfo(LOG_CONTEXT, "updateFinanceReceptionEmailSettings request", {
    hasRecipient: Boolean(payload?.recipient_email),
    hasRecipientName: Boolean(payload?.recipient_name),
    ccCount: payload?.cc_emails?.length ?? 0,
  });

  try {
    const response = await axios.put(SETTINGS_URL, payload, {
      headers: {
        Authorization: `Token ${token}`,
      },
    });

    logInfo(LOG_CONTEXT, "updateFinanceReceptionEmailSettings success", {
      updatedAt: response.data?.updated_at,
    });

    return {
      success: true,
      data: response.data,
      toast: () =>
        showToast(
          "Finance reception email settings saved.",
          "success",
          "top-right",
          3000,
        ),
    };
  } catch (error) {
    logError(
      LOG_CONTEXT,
      "updateFinanceReceptionEmailSettings failed",
      error.response?.data ?? error,
    );

    if (error.response?.status === 400 && error.response?.data) {
      return {
        success: false,
        fieldErrors: error.response.data,
      };
    }

    return {
      success: false,
      fieldErrors: error.response?.data,
    };
  }
};
